/* MatchWalking — Tinder-style 1v1 walking match (with Super Like).
   Estratto dal Match.jsx originale. Aggiunto: super like, modal intro,
   incoming super likes feed, badge saldo. Tutto Pro.
*/
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, useMotionValue, useTransform, AnimatePresence } from "framer-motion";
import {
  PawPrint, X, Star, Send, RotateCcw, Footprints, Compass, Clock,
  MessageCircle, ChevronLeft, ShoppingBag, Sparkles, Plus, Heart, Info, MapPin, Crown,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";

import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hapticSwipe, hapticSuccess, hapticTap } from "@/lib/haptics";
import SuperLikeStore from "@/components/SuperLikeStore";
import WithStoryRing, { useUnseenStories } from "@/components/WithStoryRing";

export default function MatchWalking({ onBack }) {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [phase, setPhase] = useState("loading"); // loading|setup|swiping|empty|chats
  const [walking, setWalking] = useState(null);
  const [cands, setCands] = useState([]);
  const [idx, setIdx] = useState(0);
  const [matches, setMatches] = useState([]);
  const [matchOverlay, setMatchOverlay] = useState(null);
  const [openChat, setOpenChat] = useState(null);
  const [radius, setRadius] = useState(user?.radius_prefs?.missions || 20);
  const [duration, setDuration] = useState(5);

  // Super Like
  const [slBalance, setSlBalance] = useState(0);
  const [showStore, setShowStore] = useState(false);
  const [showSuperIntro, setShowSuperIntro] = useState(null); // candidate to super-like
  const [incoming, setIncoming] = useState([]);
  const [showIncoming, setShowIncoming] = useState(false);

  const loadAll = async () => {
    try {
      const [a, ms, slb, inc] = await Promise.all([
        api.get("/match/availability/me"),
        api.get("/match/matches"),
        api.get("/match/super-likes/balance").catch(() => ({ data: { balance: 0 } })),
        api.get("/match/super-likes/incoming").catch(() => ({ data: { items: [] } })),
      ]);
      setMatches(ms.data || []);
      setSlBalance(slb.data?.balance ?? 0);
      setIncoming(inc.data?.items || []);
      if (a.data.active) {
        setWalking(a.data);
        const c = await api.get("/match/candidates");
        setCands(c.data.candidates || []);
        setIdx(0);
        setPhase(c.data.candidates?.length > 0 ? "swiping" : "empty");
      } else {
        setPhase("setup");
      }
    } catch (e) {
      if (e.response?.status === 402) toast.error("Match è una funzione ORIO Pro");
      setPhase("setup");
    }
  };
  useEffect(() => { loadAll(); }, []);

  // Deep-link: /app/match?openChat=<mid> — from MessagesInbox rows. After
  // matches load, find and open the ChatSheet automatically, then clear the
  // param so a back/forward doesn't re-trigger it.
  useEffect(() => {
    const mid = params.get("openChat");
    if (!mid || !matches.length) return;
    const target = matches.find((m) => m.match_id === mid);
    if (target) {
      setOpenChat(target);
      setPhase("chats");
      params.delete("openChat");
      setParams(params, { replace: true });
    }
  }, [matches, params, setParams]);

  const start = async () => {
    try {
      await api.post("/match/availability/start", { radius_km: radius, duration_h: duration });
      toast.success(`In passeggiata per ${duration}h · ${radius}km`);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const stop = async () => {
    await api.post("/match/availability/stop");
    setWalking(null); setCands([]); setPhase("setup");
  };

  const swipe = async (action) => {
    const c = cands[idx];
    if (!c) return;
    hapticSwipe();
    try {
      const { data } = await api.post("/match/swipe", { target_user_id: c.user.id, action });
      if (data.matched) {
        hapticSuccess();
        setMatchOverlay({ name: c.user.name, photo: c.user.photo_url, dogs: c.dogs });
        loadAll();
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    if (idx + 1 >= cands.length) setPhase("empty");
    else setIdx((i) => i + 1);
  };

  const onSuperLike = async (candidate, message) => {
    if (slBalance <= 0) {
      setShowStore(true);
      return;
    }
    try {
      const { data } = await api.post("/match/super-like", {
        target_user_id: candidate.user.id,
        message: message?.trim() || null,
      });
      hapticSuccess();
      setSlBalance(data.balance);
      toast.success("⭐ Super Like inviato!");
      setShowSuperIntro(null);
      if (data.matched) {
        setMatchOverlay({ name: candidate.user.name, photo: candidate.user.photo_url, dogs: candidate.dogs });
      }
      if (idx + 1 >= cands.length) setPhase("empty");
      else setIdx((i) => i + 1);
    } catch (e) {
      const msg = e.response?.data?.detail || "Errore";
      if (e.response?.status === 402) {
        toast.error(msg);
        setShowStore(true);
      } else {
        toast.error(msg);
      }
    }
  };

  return (
    <div className="relative min-h-[100dvh] overflow-x-hidden">
      <BrightAurora />

      <div className="relative z-10 max-w-2xl mx-auto px-4 pt-4 pb-16">
        {/* Header w/ back + super like badge */}
        <div className="flex items-center justify-between gap-3">
          <button
            onClick={onBack}
            data-testid="walking-back"
            className="h-10 w-10 rounded-full bg-white border border-[#F0EBE3] flex items-center justify-center hover:bg-[#F4EFE6]"
            aria-label="Torna alle modalità"
          >
            <ChevronLeft className="h-4 w-4 text-[#1A1A1A]" />
          </button>

          <div className="flex-1 text-center">
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#9A9A9A] inline-flex items-center gap-1.5 justify-center">
              <PawPrint className="h-3 w-3" /> Compagni di passeggiata
            </div>
            <h1 className="font-display text-2xl tracking-tight leading-tight mt-0.5">
              In <span className="orio-grad-text">passeggiata</span>
            </h1>
          </div>

          <button
            data-testid="open-super-store"
            onClick={() => { hapticTap(); setShowStore(true); }}
            className="relative h-10 px-3 rounded-full bg-white border border-[#F0EBE3] inline-flex items-center gap-1.5 hover:bg-[#F4EFE6]"
            aria-label="Saldo Super Like"
          >
            <Star className="h-4 w-4 text-[#7B61FF] fill-[#7B61FF]" />
            <span className="text-[13px] font-semibold tabular-nums" data-testid="sl-badge">{slBalance}</span>
          </button>
        </div>

        {/* Incoming Super Likes pill */}
        {incoming.length > 0 && (
          <motion.button
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
            data-testid="incoming-super-likes-btn"
            onClick={() => setShowIncoming(true)}
            className="mt-4 w-full px-4 py-3 rounded-2xl border border-[#7B61FF]/30 inline-flex items-center gap-3"
            style={{ background: "linear-gradient(135deg, rgba(123,97,255,0.08) 0%, rgba(255,122,26,0.06) 100%)" }}
          >
            <div className="h-9 w-9 rounded-xl flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, #7B61FF, #FF7A1A)" }}>
              <Star className="h-4 w-4 text-white fill-white" />
            </div>
            <div className="flex-1 text-left">
              <div className="text-[13px] font-semibold text-[#1A1A1A]">
                Hai {incoming.length} Super Like {incoming.length === 1 ? "ricevuto" : "ricevuti"}
              </div>
              <div className="text-[11px] text-[#6B6B6B]">Tocca per vedere chi vuole conoscerti</div>
            </div>
            <ChevronLeft className="h-4 w-4 text-[#9A9A9A] rotate-180" />
          </motion.button>
        )}

        {phase === "loading" && <div className="mt-12 text-[#9A9A9A] text-sm">Caricamento…</div>}

        {phase === "setup" && (
          <SetupView radius={radius} setRadius={setRadius} duration={duration} setDuration={setDuration} onStart={start} matches={matches} onChats={() => setPhase("chats")} />
        )}

        {phase === "swiping" && (
          <SwipeDeck
            cands={cands} idx={idx} walking={walking}
            onSwipe={swipe}
            onSuperLike={(c) => setShowSuperIntro(c)}
            onStop={stop}
            slBalance={slBalance}
          />
        )}

        {phase === "empty" && (
          <EmptyView walking={walking} onStop={stop} onRefresh={loadAll} />
        )}

        {phase === "chats" && (
          <MatchesList matches={matches} onBack={() => loadAll()} onOpen={(m) => setOpenChat(m)} />
        )}
      </div>

      {/* Super Like intro modal */}
      <AnimatePresence>
        {showSuperIntro && (
          <SuperIntroModal
            candidate={showSuperIntro}
            slBalance={slBalance}
            onClose={() => setShowSuperIntro(null)}
            onSend={(msg) => onSuperLike(showSuperIntro, msg)}
            onOpenStore={() => { setShowSuperIntro(null); setShowStore(true); }}
          />
        )}
      </AnimatePresence>

      {/* Incoming super likes drawer */}
      <AnimatePresence>
        {showIncoming && (
          <IncomingSuperLikes
            items={incoming}
            onClose={() => setShowIncoming(false)}
            onLikeBack={async (item) => {
              try {
                const { data } = await api.post("/match/swipe", { target_user_id: item.sender.id, action: "yes" });
                if (data.matched) {
                  hapticSuccess();
                  setMatchOverlay({ name: item.sender.name, photo: item.sender.photo_url, dogs: item.dogs });
                }
                setShowIncoming(false);
                loadAll();
              } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
            }}
            onPass={async (item) => {
              try {
                await api.post("/match/swipe", { target_user_id: item.sender.id, action: "no" });
                loadAll();
              } catch {}
            }}
          />
        )}
      </AnimatePresence>

      {/* Chat sheet */}
      <AnimatePresence>
        {openChat && <ChatSheet match={openChat} onClose={() => setOpenChat(null)} onChange={loadAll} />}
      </AnimatePresence>

      {/* Match cinematic */}
      <AnimatePresence>
        {matchOverlay && <MatchOverlay {...matchOverlay} onClose={() => setMatchOverlay(null)} />}
      </AnimatePresence>

      {/* Super Like store */}
      <SuperLikeStore
        open={showStore}
        onClose={() => setShowStore(false)}
        onPurchased={() => loadAll()}
      />
    </div>
  );
}

/* ===== Bright aurora bg ===== */
function BrightAurora() {
  return (
    <>
      <div aria-hidden className="fixed inset-0 -z-10" style={{ background: "radial-gradient(110% 70% at 50% 0%, rgba(255,176,32,0.30) 0%, rgba(0,0,0,0) 60%)" }} />
      <div aria-hidden className="fixed inset-0 -z-10" style={{ background: "radial-gradient(85% 60% at 10% 90%, rgba(255,59,48,0.22) 0%, rgba(0,0,0,0) 65%)" }} />
      <div aria-hidden className="fixed inset-0 -z-10" style={{ background: "radial-gradient(85% 60% at 95% 80%, rgba(52,211,153,0.22) 0%, rgba(0,0,0,0) 65%)" }} />
    </>
  );
}

/* ===== Setup ===== */
function SetupView({ radius, setRadius, duration, setDuration, onStart, matches, onChats }) {
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-7" data-testid="match-setup">
      <p className="text-[#1A1A1A] text-[15px] leading-relaxed">
        Imposta dove vuoi camminare e per quanto. Quando esci di casa col cane, attiva la modalità: vedrai chi è in passeggiata
        vicino a te <strong>adesso</strong>, niente di più.
      </p>

      <div className="mt-6 rounded-3xl p-5 border border-[#F0EBE3] bg-white">
        <RadiusSlider label="Quanto vuoi camminare via?" sub="Raggio entro cui cercare compagnia"
          value={radius} setValue={setRadius} min={1} max={50} unit="km" color="#FFB020" icon={Compass} testid="match-radius" />
        <div className="h-5" />
        <RadiusSlider label="Per quanto tempo?" sub="Modalità si spegne da sola"
          value={duration} setValue={setDuration} min={1} max={5} unit="h" color="#34D399" icon={Clock} testid="match-duration" />
      </div>

      <motion.button whileTap={{ scale: 0.96 }} data-testid="match-start" onClick={onStart}
        className="mt-6 w-full py-4 rounded-full font-display text-lg text-white font-semibold inline-flex items-center justify-center gap-2"
        style={{ background: "linear-gradient(120deg, #FFB020 0%, #FF3B30 50%, #34D399 100%)" }}>
        <Footprints className="h-5 w-5" /> Sono in passeggiata, fammi trovare
      </motion.button>

      {matches.length > 0 && (
        <button onClick={onChats} data-testid="open-matches-list" className="mt-3 w-full py-3 rounded-full border border-[#F0EBE3] inline-flex items-center justify-center gap-2 text-sm hover:bg-[#F4EFE6]">
          <MessageCircle className="h-4 w-4" /> I tuoi {matches.length} match
        </button>
      )}
    </motion.div>
  );
}

function RadiusSlider({ label, sub, value, setValue, min, max, unit, color, icon: Icon, testid }) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${color}25`, color, border: `1px solid ${color}55` }}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[15px] text-[#1A1A1A] font-medium">{label}</div>
          <div className="text-[11px] text-[#9A9A9A] mt-0.5">{sub}</div>
        </div>
        <div className="font-display text-2xl tabular-nums" style={{ color }}>
          {value}<span className="text-sm text-[#9A9A9A] ml-0.5">{unit}</span>
        </div>
      </div>
      <input type="range" min={min} max={max} step={1} value={value} onChange={(e) => setValue(parseInt(e.target.value))}
        data-testid={`${testid}-input`}
        className="w-full h-1.5 rounded-full appearance-none cursor-pointer mt-3 focus:outline-none"
        style={{ background: `linear-gradient(to right, ${color} 0%, ${color} ${pct}%, rgba(0,0,0,0.10) ${pct}%, rgba(0,0,0,0.10) 100%)` }} />
    </div>
  );
}

/* ===== Empty ===== */
function EmptyView({ walking, onStop, onRefresh }) {
  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="mt-10 text-center" data-testid="match-empty">
      <div className="h-24 w-24 mx-auto rounded-3xl flex items-center justify-center border border-[#F0EBE3] bg-white">
        <PawPrint className="h-10 w-10 text-[#6B6B6B]" />
      </div>
      <div className="font-display text-2xl mt-5">Nessuno in giro adesso</div>
      <p className="text-[13px] text-[#6B6B6B] mt-2 max-w-md mx-auto">
        Nessun cane sta camminando nel tuo raggio. Resta in modalità — appena qualcuno esce ti avvisiamo.
      </p>
      <div className="mt-6 flex flex-col items-center gap-2">
        <button onClick={onRefresh} data-testid="match-refresh" className="px-5 py-2.5 rounded-full bg-white border border-[#F0EBE3] text-sm inline-flex items-center gap-2 hover:bg-[#F4EFE6]">
          <RotateCcw className="h-4 w-4" /> Riprova
        </button>
        <button onClick={onStop} data-testid="match-stop" className="text-[12px] text-[#9A9A9A] hover:text-[#1A1A1A]">Esci dalla passeggiata</button>
      </div>
    </motion.div>
  );
}

/* ===== Swipe deck ===== */
function SwipeDeck({ cands, idx, walking, onSwipe, onSuperLike, onStop, slBalance }) {
  const current = cands[idx];
  const next = cands[idx + 1];
  if (!current) return null;
  return (
    <div className="mt-3" data-testid="match-deck">
      {walking && (
        <div className="text-[11px] text-[#6B6B6B] mb-3 inline-flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-[#34D399] animate-pulse" />
          In passeggiata · raggio {walking.radius_km || walking.radius || "—"} km
          <button onClick={onStop} className="ml-2 text-[#9A9A9A] hover:text-[#1A1A1A] underline-offset-2 hover:underline">stop</button>
        </div>
      )}

      <div className="relative" style={{ aspectRatio: "3/4", maxHeight: "62vh" }}>
        {next && <SwipeCard key={`next-${next.user.id}`} c={next} style={{ scale: 0.94, opacity: 0.6 }} faded />}
        <SwipeCard key={`top-${current.user.id}`} c={current} onSwipe={onSwipe} top />
      </div>

      {/* 3-button action row: pass / super / like */}
      <div className="mt-5 flex items-center justify-center gap-4">
        <motion.button whileTap={{ scale: 0.88 }} data-testid="match-skip"
          onClick={() => onSwipe("no")}
          className="h-14 w-14 rounded-full flex items-center justify-center border-2 border-[#F0EBE3] bg-white"
          aria-label="Sarà un'altra volta">
          <X className="h-6 w-6 text-[#6B6B6B]" />
        </motion.button>

        <motion.button whileTap={{ scale: 0.86 }}
          whileHover={{ y: -2 }}
          data-testid="match-super"
          onClick={() => onSuperLike(current)}
          className="relative h-16 w-16 rounded-full flex items-center justify-center text-white"
          style={{
            background: "linear-gradient(135deg, #7B61FF 0%, #FF7A1A 100%)",
            boxShadow: "0 0 28px rgba(123,97,255,0.55), 0 12px 26px -10px rgba(0,0,0,0.30)",
          }}
          aria-label="Super Like">
          <Star className="h-6 w-6 fill-white" />
          {slBalance > 0 && (
            <span className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-white text-[#7B61FF] text-[10px] font-bold flex items-center justify-center border border-[#7B61FF]/30">
              {slBalance}
            </span>
          )}
        </motion.button>

        <motion.button whileTap={{ scale: 0.88 }} data-testid="match-propose"
          onClick={() => onSwipe("yes")}
          className="h-14 w-14 rounded-full flex items-center justify-center text-white"
          style={{ background: "linear-gradient(135deg, #FFB020 0%, #34D399 100%)", boxShadow: "0 0 28px rgba(52,211,153,0.45)" }}
          aria-label="Proponi passeggiata">
          <PawPrint className="h-6 w-6" />
        </motion.button>
      </div>
      <div className="mt-3 text-center text-[11px] text-[#9A9A9A]">
        Salta · <strong className="text-[#7B61FF]">Super Like</strong> · <strong className="text-[#1A1A1A]">Proponi</strong>
      </div>
    </div>
  );
}

function SwipeCard({ c, onSwipe, top = false, faded = false, style }) {
  const navigate = useNavigate();
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-220, 220], [-15, 15]);
  const opacity = useTransform(x, [-220, -120, 0, 120, 220], [0, 1, 1, 1, 0]);
  const yesGlow = useTransform(x, [0, 140], [0, 1]);
  const noGlow = useTransform(x, [0, -140], [0, 1]);
  const ref = useRef(null);
  const u = c.user; const dog = c.dogs?.[0];

  // ─── Photo carousel — Tinder-style multi-photo swipe with tap zones
  //     for prev/next and dotted progress bar at the top.
  const photos = useMemo(() => {
    const arr = (u.photos && u.photos.length > 0)
      ? u.photos
      : (u.photo_url ? [u.photo_url] : []);
    if (arr.length === 0 && dog?.photo_url) return [dog.photo_url];
    return arr;
  }, [u, dog]);
  const [photoIdx, setPhotoIdx] = useState(0);
  useEffect(() => { setPhotoIdx(0); }, [u.id]);
  const photo = photos[photoIdx] || photos[0];

  const onEnd = (_, info) => {
    if (info.offset.x > 120) onSwipe?.("yes");
    else if (info.offset.x < -120) onSwipe?.("no");
  };

  return (
    <motion.div ref={ref} data-testid={`swipe-card-${u.id}`}
      drag={top ? "x" : false}
      style={{
        x: top ? x : 0,
        rotate: top ? rotate : 0,
        opacity: top ? opacity : (style?.opacity ?? 1),
        scale: style?.scale ?? 1,
        boxShadow: "0 30px 60px -20px rgba(0,0,0,0.32)",
      }}
      dragConstraints={{ left: 0, right: 0 }} dragElastic={0.7} onDragEnd={onEnd}
      className={`absolute inset-0 rounded-[28px] overflow-hidden border border-[#F0EBE3] bg-black ${faded ? "pointer-events-none" : ""}`}
    >
      {/* Photo layer + tap-zones for carousel navigation (top only). */}
      <div className="absolute inset-0">
        {photo ? (
          <img src={photo} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center"
               style={{ background: "linear-gradient(135deg,#FF7A1A,#F4B83C)" }}>
            <PawPrint className="h-24 w-24 text-white/70" />
          </div>
        )}
        {/* Segmented progress dots (top) — Tinder-style */}
        {top && photos.length > 1 && (
          <div className="absolute top-2.5 inset-x-2.5 z-30 flex gap-1 pointer-events-none">
            {photos.map((_, i) => (
              <span key={i} className="h-[3px] flex-1 rounded-full"
                    style={{ background: i === photoIdx ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.30)" }} />
            ))}
          </div>
        )}
        {/* Tap zones — invisible left/right buttons to switch photo. Must
            not swallow the drag gesture, so we absorb only click events. */}
        {top && photos.length > 1 && (
          <>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setPhotoIdx((i) => (i - 1 + photos.length) % photos.length); }}
              onPointerDownCapture={(e) => e.stopPropagation()}
              aria-label="Foto precedente"
              className="absolute inset-y-0 left-0 w-1/3 z-20"
              data-testid="swipe-card-photo-prev"
            />
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setPhotoIdx((i) => (i + 1) % photos.length); }}
              onPointerDownCapture={(e) => e.stopPropagation()}
              aria-label="Foto successiva"
              className="absolute inset-y-0 right-0 w-1/3 z-20"
              data-testid="swipe-card-photo-next"
            />
          </>
        )}
        {/* Two-stop legibility gradient — subtle up top, strong at bottom */}
        <div className="absolute inset-0 pointer-events-none"
             style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.28) 0%, rgba(0,0,0,0) 22%, rgba(0,0,0,0) 46%, rgba(0,0,0,0.55) 78%, rgba(0,0,0,0.88) 100%)" }} />
      </div>

      {/* Swipe direction badges */}
      {top && (
        <>
          <motion.div style={{ opacity: yesGlow }} className="absolute inset-0 pointer-events-none z-10">
            <div className="absolute top-16 left-6 px-3 py-1.5 rounded-xl rotate-[-14deg] font-display text-2xl border-2"
                 style={{ background: "rgba(52,211,153,0.18)", color: "#34D399", borderColor: "#34D399" }}>PROPONGO</div>
          </motion.div>
          <motion.div style={{ opacity: noGlow }} className="absolute inset-0 pointer-events-none z-10">
            <div className="absolute top-16 right-6 px-3 py-1.5 rounded-xl rotate-[12deg] font-display text-2xl border-2"
                 style={{ background: "rgba(255,176,32,0.18)", color: "#FFB020", borderColor: "#FFB020" }}>UN&apos;ALTRA VOLTA</div>
          </motion.div>
        </>
      )}

      {/* Top-left: distance chip — the single most valuable info at a glance */}
      {top && c.distance_km != null && (
        <div className="absolute top-6 left-4 z-20">
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-bold text-white"
            style={{
              background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
              boxShadow: "0 10px 20px -8px rgba(16,185,129,0.55)",
            }}
          >
            <MapPin className="h-3.5 w-3.5" strokeWidth={2.5} />
            {c.distance_km < 1 ? `${Math.round(c.distance_km * 1000)}m` : `${c.distance_km} km`}
          </span>
        </div>
      )}

      {/* Top-right: profile button + PRO chip (if any) */}
      {top && (
        <div className="absolute top-6 right-4 z-20 flex items-center gap-1.5">
          {u.is_pro && (
            <span
              className="inline-flex items-center gap-1 px-2 py-1.5 rounded-full text-[10px] font-bold text-white"
              style={{
                background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)",
                boxShadow: "0 6px 14px -6px rgba(255,122,26,0.55)",
              }}
            >
              <Crown className="h-3 w-3" strokeWidth={2.5} /> PRO
            </span>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); navigate(`/app/u/${u.id}`); }}
            onPointerDownCapture={(e) => e.stopPropagation()}
            aria-label={`Apri profilo di ${u.name || "utente"}`}
            data-testid={`swipe-card-open-profile-${u.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold text-white active:scale-95"
            style={{
              background: "rgba(0,0,0,0.55)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255,255,255,0.35)",
            }}
          >
            <Info className="h-3 w-3" /> Profilo
          </button>
        </div>
      )}

      {/* Bottom identity block */}
      <div className="absolute inset-x-0 bottom-0 p-5 text-white z-10">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); navigate(`/app/u/${u.id}`); }}
          onPointerDownCapture={(e) => e.stopPropagation()}
          className="font-display text-[32px] leading-none text-white text-left active:opacity-80 tracking-tight"
          data-testid={`swipe-card-name-${u.id}`}
          style={{ textShadow: "0 2px 12px rgba(0,0,0,0.55)" }}
        >
          {u.name}
          {u.age != null && <span className="text-white/85 font-normal ml-2">{u.age}</span>}
        </button>
        {u.city && (
          <div className="mt-1.5 inline-flex items-center gap-1 text-[13px] text-white/85"
               style={{ textShadow: "0 1px 4px rgba(0,0,0,0.55)" }}>
            <MapPin className="h-3.5 w-3.5" /> {u.city}
          </div>
        )}
        {u.bio && (
          <p className="text-[13px] text-white/85 mt-2 line-clamp-2 leading-snug"
             style={{ textShadow: "0 1px 4px rgba(0,0,0,0.55)" }}>
            {u.bio}
          </p>
        )}
        {/* Dog chip strip — bigger, cinematic-looking */}
        {(c.dogs || []).length > 0 && (
          <div className="mt-3 flex gap-2 flex-wrap">
            {(c.dogs || []).slice(0, 2).map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={(e) => { e.stopPropagation(); navigate(`/app/u/${u.id}`); }}
                onPointerDownCapture={(e) => e.stopPropagation()}
                className="flex items-center gap-2 rounded-full bg-white/18 backdrop-blur-md border border-white/25 pl-1 pr-3.5 py-1 active:scale-95"
                data-testid={`swipe-card-dog-${d.id}`}
              >
                {d.photo_url ? (
                  <img src={d.photo_url} alt={d.name} className="h-8 w-8 rounded-full object-cover" />
                ) : (
                  <div className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center">
                    <PawPrint className="h-4 w-4" />
                  </div>
                )}
                <div className="leading-tight text-left">
                  <div className="text-[13px] font-semibold text-white">{d.name}</div>
                  {d.breed && <div className="text-[10px] text-white/80 -mt-0.5">{d.breed}</div>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}

/* ===== Super Like intro modal ===== */
function SuperIntroModal({ candidate, slBalance, onClose, onSend, onOpenStore }) {
  const [message, setMessage] = useState("");
  const u = candidate.user;
  const insufficient = slBalance <= 0;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[1400] flex items-end sm:items-center justify-center pb-[160px] sm:pb-0" data-testid="super-intro-modal">
      <div className="absolute inset-0 bg-black/45 backdrop-blur-md" onClick={onClose} />
      <motion.div
        initial={{ y: 60, scale: 0.95 }} animate={{ y: 0, scale: 1 }} exit={{ y: 60, scale: 0.95 }}
        transition={{ type: "spring", damping: 24, stiffness: 280 }}
        className="relative w-full sm:max-w-md mx-3 sm:mx-0 rounded-t-[28px] sm:rounded-[28px] bg-white border border-[#F0EBE3] overflow-hidden"
        style={{ maxHeight: "92dvh" }}
      >
        <div className="relative px-5 pt-6 pb-4 text-center">
          <motion.div aria-hidden className="absolute -top-16 left-1/2 -translate-x-1/2 h-48 w-48 rounded-full"
            style={{ background: "radial-gradient(circle, rgba(123,97,255,0.30) 0%, transparent 60%)" }}
            animate={{ scale: [1, 1.12, 1] }} transition={{ duration: 3, repeat: Infinity }} />
          <motion.div initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", damping: 14, stiffness: 200 }}
            className="relative mx-auto h-16 w-16 rounded-full flex items-center justify-center"
            style={{ background: "linear-gradient(135deg, #7B61FF 0%, #FF7A1A 100%)", boxShadow: "0 0 36px rgba(123,97,255,0.55)" }}>
            <Star className="h-7 w-7 text-white fill-white" />
          </motion.div>
          <h2 className="mt-3 font-display text-2xl text-[#1A1A1A] leading-tight">
            Super Like a <span style={{ background: "linear-gradient(135deg, #7B61FF 0%, #FF7A1A 100%)", WebkitBackgroundClip: "text", color: "transparent" }}>{u.name}</span>
          </h2>
          <p className="mt-1 text-[12px] text-[#6B6B6B] px-4">
            Saluti subito {u.name} con un messaggio. Vedrà profilo + cane senza match. Risponde se ti dà like.
          </p>
        </div>

        <div className="px-5 pb-4">
          <label className="text-[10px] uppercase tracking-widest text-[#9A9A9A] font-semibold">Il tuo messaggio (opzionale)</label>
          <textarea
            data-testid="super-intro-text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={`Ciao ${u.name}, mi piacerebbe fare una passeggiata insieme…`}
            maxLength={240}
            rows={3}
            className="mt-1 w-full px-3.5 py-3 rounded-2xl bg-[#FBF7F1] border border-[#F0EBE3] text-[14px] text-[#1A1A1A] resize-none outline-none focus:border-[#7B61FF]/40 focus:bg-white"
          />
          <div className="mt-1 text-right text-[10px] text-[#9A9A9A]">{message.length}/240</div>
        </div>

        <div className="px-5 pb-5 pt-2 border-t border-[#F0EBE3] bg-[#FBF7F1]">
          {insufficient ? (
            <motion.button whileTap={{ scale: 0.97 }} onClick={onOpenStore} data-testid="super-intro-buy"
              className="w-full py-3.5 rounded-full text-white font-semibold text-[14px] inline-flex items-center justify-center gap-2"
              style={{ background: "linear-gradient(135deg, #7B61FF 0%, #FF7A1A 100%)" }}>
              <ShoppingBag className="h-4 w-4" /> Saldo esaurito · Acquista Super Like
            </motion.button>
          ) : (
            <motion.button whileTap={{ scale: 0.97 }} onClick={() => onSend(message)} data-testid="super-intro-send"
              className="w-full py-3.5 rounded-full text-white font-semibold text-[14px] inline-flex items-center justify-center gap-2"
              style={{ background: "linear-gradient(135deg, #7B61FF 0%, #FF7A1A 100%)", boxShadow: "0 14px 30px -10px rgba(123,97,255,0.55)" }}>
              <Send className="h-4 w-4" /> Invia Super Like ({slBalance - 1} rimanenti)
            </motion.button>
          )}
          <button type="button" onClick={onClose} className="mt-2 w-full py-2 text-[12px] text-[#9A9A9A] hover:text-[#1A1A1A]">
            Annulla
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ===== Incoming Super Likes ===== */
function IncomingSuperLikes({ items, onClose, onLikeBack, onPass }) {
  const { unseen } = useUnseenStories(items.map((it) => it.sender.id));
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[1380] flex items-end sm:items-center justify-center pb-[160px] sm:pb-0" data-testid="incoming-super-likes-drawer">
      <div className="absolute inset-0 bg-black/45 backdrop-blur-md" onClick={onClose} />
      <motion.div initial={{ y: 80 }} animate={{ y: 0 }} exit={{ y: 80 }}
        transition={{ type: "spring", damping: 26, stiffness: 280 }}
        className="relative w-full sm:max-w-md mx-3 sm:mx-0 rounded-t-[28px] sm:rounded-[28px] bg-white border border-[#F0EBE3] overflow-hidden flex flex-col"
        style={{ maxHeight: "92dvh" }}>
        <div className="px-5 pt-5 pb-3 border-b border-[#F0EBE3] flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#7B61FF] inline-flex items-center gap-1.5">
              <Star className="h-3 w-3 fill-[#7B61FF]" /> Super Like ricevuti
            </div>
            <h2 className="mt-1 font-display text-2xl text-[#1A1A1A]">{items.length} {items.length === 1 ? "persona" : "persone"} vogliono conoscerti</h2>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-full bg-[#F4EFE6] flex items-center justify-center"><X className="h-4 w-4" /></button>
        </div>
        <div className="overflow-y-auto p-3 space-y-3" style={{ maxHeight: "70dvh" }}>
          {items.map((it) => (
            <motion.div key={it.swipe_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl border-2 border-[#7B61FF]/25 overflow-hidden"
              style={{ background: "linear-gradient(135deg, rgba(123,97,255,0.05) 0%, rgba(255,122,26,0.04) 100%)" }}
              data-testid={`super-incoming-${it.sender.id}`}>
              <div className="flex items-start gap-3 p-3">
                <WithStoryRing unseen={unseen} userId={it.sender.id} size={58}>
                  {it.sender.photo_url ? (
                    <img src={it.sender.photo_url} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <div className="h-full w-full bg-[#EAE3D2] flex items-center justify-center">
                      <PawPrint className="h-6 w-6 text-[#9A9A9A]" />
                    </div>
                  )}
                </WithStoryRing>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg text-[#1A1A1A] leading-tight">{it.sender.name}</div>
                  <div className="text-[11px] text-[#6B6B6B]">
                    {it.sender.city || "—"} · {(it.dogs || []).map(d => d.name).join(", ") || "Senza cane"}
                  </div>
                </div>
              </div>
              {it.message && (
                <div className="mx-3 mb-3 px-3 py-2.5 rounded-2xl text-[13px] text-[#1A1A1A] leading-snug"
                  style={{ background: "linear-gradient(135deg, #7B61FF 0%, #FF7A1A 100%)", color: "white" }}>
                  “{it.message}”
                </div>
              )}
              <div className="px-3 pb-3 flex gap-2">
                <button data-testid={`super-pass-${it.sender.id}`}
                  onClick={() => onPass(it)}
                  className="flex-1 py-2.5 rounded-full bg-white border border-[#F0EBE3] text-[#6B6B6B] text-[13px] hover:bg-[#F4EFE6]">
                  Salta
                </button>
                <button data-testid={`super-likeback-${it.sender.id}`}
                  onClick={() => onLikeBack(it)}
                  className="flex-1 py-2.5 rounded-full text-white text-[13px] font-semibold inline-flex items-center justify-center gap-1.5"
                  style={{ background: "linear-gradient(135deg, #FFB020 0%, #34D399 100%)" }}>
                  <Heart className="h-4 w-4 fill-white" /> Mi piace
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ===== Matches list (existing, unchanged) ===== */
function MatchesList({ matches, onBack, onOpen }) {
  const { unseen } = useUnseenStories(matches.map((m) => m.other?.id).filter(Boolean));
  return (
    <div className="mt-6" data-testid="match-chats-list">
      <button onClick={onBack} className="text-xs text-[#9A9A9A] hover:text-[#1A1A1A] inline-flex items-center gap-1 mb-3">
        <ChevronLeft className="h-3 w-3" /> Torna ai match
      </button>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#9A9A9A]">I tuoi accordi</div>
      <h2 className="font-display text-2xl mt-1">Compagni con cui parlare</h2>
      {matches.length === 0 ? (
        <div className="mt-8 text-[#9A9A9A] text-sm">Nessun match ancora.</div>
      ) : (
        <div className="mt-5 space-y-2">
          {matches.map((m) => (
            <motion.button key={m.match_id} whileTap={{ scale: 0.98 }} onClick={() => !m.expired && onOpen(m)}
              disabled={m.expired}
              data-testid={`match-row-${m.match_id}`}
              className={`w-full flex items-center gap-3 p-3 rounded-2xl border text-left bg-white ${m.expired ? "border-[#E5E0D8] opacity-70 grayscale cursor-default" : "border-[#F0EBE3] hover:bg-[#F4EFE6]"}`}>
              <WithStoryRing unseen={unseen} userId={m.other?.id} size={50}>
                {m.other.photo_url
                  ? <img src={m.other.photo_url} alt="" className="h-full w-full object-cover" />
                  : <div className="h-full w-full bg-[#EAE3D2] flex items-center justify-center"><PawPrint className="h-5 w-5" /></div>}
              </WithStoryRing>
              <div className="flex-1 min-w-0">
                <div className="font-medium">{m.other.name}</div>
                <div className="text-[11px] text-[#6B6B6B] truncate">
                  {(m.dogs || []).map((d) => d.name).join(" · ") || "Senza cane"}
                </div>
              </div>
              <ExpiryPill match={m} />
              <MessageCircle className="h-4 w-4 text-[#9A9A9A]" />
            </motion.button>
          ))}
        </div>
      )}
    </div>
  );
}

function hoursLeft(m) {
  if (m.expired || !m.expires_at) return null;
  return Math.max(1, Math.ceil((new Date(m.expires_at) - Date.now()) / 3600000));
}

function ExpiryPill({ match }) {
  if (match.expired) {
    return <span data-testid={`match-expired-${match.match_id}`} className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-[#EDE8E0] text-[#6B6B6B]">Scaduto</span>;
  }
  const h = hoursLeft(match);
  if (h == null) return null;
  return <span data-testid={`match-expires-${match.match_id}`} className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#FFF3E0] text-[#C25612]">⏳ {h}h</span>;
}

/* ===== Chat sheet (kept same logic, lightened to warm) ===== */
function ChatSheet({ match, onClose, onChange }) {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [remaining, setRemaining] = useState(10);
  const [expired, setExpired] = useState(!!match.expired);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [ratings, setRatings] = useState({ my: null, other: null });
  const [canRate, setCanRate] = useState(false);
  const [rating, setRating] = useState({ owner_score: 5, dog_score: 5, note: "" });
  const scrollRef = useRef(null);

  const load = async () => {
    const [m, r] = await Promise.all([
      api.get(`/match/${match.match_id}/messages`),
      api.get(`/match/${match.match_id}/ratings`),
    ]);
    setMsgs(m.data.messages); setRemaining(m.data.remaining); setRatings(r.data);
    setExpired(!!m.data.expired);
    const created = new Date(match.created_at);
    setCanRate((Date.now() - created.getTime()) > 24 * 3600 * 1000);
    setTimeout(() => scrollRef.current?.scrollTo({ top: 999999, behavior: "smooth" }), 50);
  };
  useEffect(() => { load(); }, [match.match_id]); // eslint-disable-line

  useEffect(() => {
    document.body.classList.add("orio-chat-open");
    return () => { document.body.classList.remove("orio-chat-open"); };
  }, []);

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/match/${match.match_id}/messages`, { text });
      setMsgs((arr) => [...arr, data.message]);
      setRemaining(data.remaining); setText("");
      setTimeout(() => scrollRef.current?.scrollTo({ top: 999999, behavior: "smooth" }), 50);
    } catch (e) {
      if (e.response?.status === 410) setExpired(true);
      toast.error(e.response?.data?.detail || "Errore");
    }
    finally { setSending(false); }
  };
  const submitRate = async () => {
    try { await api.post(`/match/${match.match_id}/rate`, rating); toast.success("Grazie · +5 karma"); load(); onChange?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[1300] flex items-end sm:items-center justify-center pb-14 sm:pb-0" data-testid="chat-sheet">
      <div className="absolute inset-0 bg-black/35 backdrop-blur-md" onClick={onClose} />
      <motion.div initial={{ y: 60 }} animate={{ y: 0 }} exit={{ y: 60 }}
        transition={{ type: "spring", damping: 28, stiffness: 280 }}
        className="relative w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl border border-[#F0EBE3] bg-white overflow-hidden flex flex-col"
        style={{ maxHeight: "min(94dvh, calc(100dvh - 70px))" }}>
        <div className="p-4 flex items-center gap-3 border-b border-[#F0EBE3]">
          {match.other.photo_url ? <img src={match.other.photo_url} alt="" className="h-10 w-10 rounded-xl object-cover" /> :
            <div className="h-10 w-10 rounded-xl bg-[#EAE3D2] flex items-center justify-center"><PawPrint className="h-4 w-4" /></div>}
          <div className="flex-1 min-w-0">
            <div className="font-medium leading-tight text-[#1A1A1A]">{match.other.name}</div>
            <div className="text-[11px] text-[#9A9A9A] truncate">{(match.dogs || []).map((d) => d.name).join(" · ")}</div>
          </div>
          <button onClick={onClose} className="h-8 w-8 rounded-full bg-[#F4EFE6] hover:bg-[#EAE3D2] flex items-center justify-center"><X className="h-4 w-4" /></button>
        </div>
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-2" style={{ minHeight: 220 }}>
          {msgs.length === 0 && !expired && (
            <div className="text-[12px] text-[#9A9A9A] text-center py-8">
              Avete 10 messaggi per accordarvi.<br /> Niente chat infinita: scegliete dove e quando, poi a piedi.
              {!expired && hoursLeft(match) != null && (
                <div className="mt-3 inline-block px-3 py-1 rounded-full bg-[#FFF3E0] text-[#C25612] font-semibold" data-testid="chat-expiry-hint">
                  ⏳ Scrivete entro {hoursLeft(match)}h o il match scade
                </div>
              )}
            </div>
          )}
          {msgs.map((m) => {
            const mine = m.from_id === user?.id;
            return (
              <motion.div key={m.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                className={`max-w-[78%] px-3.5 py-2 rounded-2xl text-[14px] ${mine ? "ml-auto text-white" : "mr-auto text-[#1A1A1A]"}`}
                style={{ background: mine ? "linear-gradient(135deg, #FFB020 0%, #34D399 100%)" : "#F4EFE6" }}>
                {m.text}
              </motion.div>
            );
          })}
        </div>

        {canRate && !expired && msgs.length > 0 && !ratings.my && (
          <div className="border-t border-[#F0EBE3] p-4">
            <div className="text-[10px] uppercase tracking-widest text-[#FF7A1A]">Com&apos;è andata?</div>
            <div className="text-[13px] mt-1 text-[#1A1A1A]">Valuta {match.other.name} (+5 karma)</div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <RatingStars label="Padrone" value={rating.owner_score} onChange={(v) => setRating((r) => ({ ...r, owner_score: v }))} testid="rate-owner" />
              <RatingStars label="Cane" value={rating.dog_score} onChange={(v) => setRating((r) => ({ ...r, dog_score: v }))} testid="rate-dog" />
            </div>
            <button onClick={submitRate} data-testid="rate-submit"
              className="mt-3 w-full py-2.5 rounded-full text-white font-semibold text-sm"
              style={{ background: "linear-gradient(135deg, #FFB020 0%, #34D399 100%)" }}>
              Lascia valutazione
            </button>
          </div>
        )}

        {expired ? (
          <div className="border-t border-[#F0EBE3] px-4 py-4 text-center bg-[#F4EFE6]" data-testid="chat-expired-banner">
            <div className="text-[13px] font-semibold text-[#1A1A1A]">⌛ Match scaduto</div>
            <div className="text-[12px] text-[#7A7A7A] mt-0.5">Nessun messaggio in 3 giorni. Rimettiti in passeggiata per nuovi match!</div>
          </div>
        ) : remaining > 0 ? (
          <div className="border-t border-[#F0EBE3] p-3 flex items-center gap-2">
            <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={`Scrivi… (${remaining} rimanent${remaining === 1 ? "e" : "i"})`} maxLength={500}
              className="flex-1 px-3.5 py-2.5 rounded-full bg-[#FBF7F1] border border-[#F0EBE3] outline-none focus:border-[#FF7A1A]/40 text-sm text-[#1A1A1A]" />
            <motion.button whileTap={{ scale: 0.92 }} onClick={send} disabled={!text.trim() || sending} data-testid="chat-send"
              className="h-10 w-10 rounded-full text-white flex items-center justify-center disabled:opacity-40"
              style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)" }}>
              <Send className="h-4 w-4" />
            </motion.button>
          </div>
        ) : (
          <div className="border-t border-[#F0EBE3] px-4 py-3 text-[12px] text-center text-[#9A9A9A]">
            10 messaggi finiti. Ora a piedi, in passeggiata, vi accordate dal vivo!
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

function RatingStars({ label, value, onChange, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-[#9A9A9A]">{label}</div>
      <div className="flex gap-1 mt-1.5">
        {[1, 2, 3, 4, 5].map((v) => (
          <button key={v} type="button" onClick={() => onChange(v)} data-testid={`${testid}-${v}`} className="transition-transform active:scale-90">
            <Star className={`h-5 w-5 ${v <= value ? "text-[#FFB020] fill-[#FFB020]" : "text-[#1A1A1A]/25"}`} />
          </button>
        ))}
      </div>
    </div>
  );
}

/* ===== Match overlay ===== */
function MatchOverlay({ name, photo, dogs, onClose }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[1400] flex items-center justify-center px-6" data-testid="match-overlay">
      <div className="absolute inset-0 backdrop-blur-2xl"
        style={{ background: "radial-gradient(60% 60% at 50% 40%, rgba(52,211,153,0.30) 0%, rgba(0,0,0,0.55) 70%)" }} />
      <motion.div initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", damping: 18, stiffness: 200 }} className="relative text-center max-w-xs">
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: "spring", damping: 16 }} className="mx-auto">
          <div className="relative h-28 w-28 mx-auto">
            {photo && <img src={photo} alt="" className="h-full w-full rounded-full object-cover border-4 border-white" />}
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 8, repeat: Infinity, ease: "linear" }} className="absolute inset-0 rounded-full"
              style={{
                background: "conic-gradient(from 0deg, #FFB020, #34D399, #FF3B30, #FFB020)", padding: 3,
                WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                WebkitMaskComposite: "xor", maskComposite: "exclude",
              }} />
          </div>
        </motion.div>
        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.45 }}
          className="font-display text-5xl tracking-tight mt-6 leading-none"
          style={{ background: "linear-gradient(135deg, #FFB020 0%, #34D399 100%)", WebkitBackgroundClip: "text", color: "transparent" }}>
          Zampe in coro!
        </motion.div>
        <motion.div initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.6 }}
          className="mt-3 text-white text-[15px] leading-relaxed">
          Tu e <strong>{name}</strong>{dogs?.[0] ? ` con ${dogs.map((d) => d.name).join(" e ")}` : ""} potete organizzare una passeggiata.
        </motion.div>
        <motion.button initial={{ y: 14, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.8 }}
          whileTap={{ scale: 0.95 }} onClick={onClose} data-testid="overlay-close"
          className="mt-6 px-6 py-3 rounded-full bg-white text-[#1A1A1A] font-semibold text-sm">
          Continua a sfogliare
        </motion.button>
      </motion.div>
    </motion.div>
  );
}
